"use client";
import { useAreaContext } from "@/app/_contexts/Area/AreaContext";
import useFormDraftPersist from "@/app/_hooks/useFormDraftPersist";
import { setBookingForm } from "@/app/_store/slices/formMemorySlice";
import { brandFont } from "@/app/styles/fonts";
import { usePathname, useRouter } from "@/i18n/navigation";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Logo from "../../../public/images/Logo.png";
import NavbarNavItem from "./NavbarNavItem";
import UserMenu from "./UserMenu";
import { NAVBAR_ICONS, useNavbarItems } from "./useNavbarItems";

export const SIDEBAR_WIDTH = 236;

export default function Navbar() {
  const router = useRouter();
  const { availableAreas } = useAreaContext();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "ADMIN";
  const dispatch = useDispatch();
  const { clear: clearEvent } = useFormDraftPersist("eventFormData");
  const currentPath = usePathname() ?? "";
  const hasServiceArea = availableAreas.includes("service");
  const { navItems, activeAreaLabel, tTabs } = useNavbarItems(
    currentPath,
    hasServiceArea,
  );
  const activeColor = theme.palette.primary.main;

  const userDisplayLabel = useMemo(
    () => session?.user?.name || session?.user?.email || "",
    [session],
  );
  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleNavClick = (href) => {
    dispatch(setBookingForm(null));
    clearEvent();
    router.push(href);
    if (!isDesktop) setMobileOpen(false);
  };

  return (
    <Box
      component="nav"
      sx={{ width: { lg: SIDEBAR_WIDTH }, flexShrink: { lg: 0 } }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{ display: { lg: "none" } }}
      >
        <Toolbar sx={{ minHeight: 56, px: 1.5 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="subtitle1"
            noWrap
            component="div"
            fontWeight={700}
          >
            {activeAreaLabel}
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isDesktop ? "permanent" : "temporary"}
        open={isDesktop ? true : mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: SIDEBAR_WIDTH, borderRight: "none" } }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2.25,
            py: 2,
            px: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1.3 }}>
            <Image
              src={Logo}
              alt="PenMan Logo"
              width={32}
              height={32}
              className="shrink-0"
              style={{ width: 32, height: 32, objectFit: "contain" }}
            />
            <Typography
              variant="subtitle1"
              fontWeight={700}
              className={brandFont.className}
              sx={{ color: "#E66308", letterSpacing: "0.015em" }}
            >
              PetMan
            </Typography>
          </Box>
          <List
            sx={{
              flexGrow: 1,
              px: 0,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            {navItems.map((item) => (
              <NavbarNavItem
                key={item.href}
                href={item.href}
                label={item.label}
                icon={item.icon}
                isActive={item.isActive}
                onClick={handleNavClick}
                activeColor={activeColor}
              />
            ))}
            {isAdmin && (
              <>
                <Divider sx={{ my: 1, mx: 1, borderColor: "#e2e8f0" }} />
                <NavbarNavItem
                  href="/admin"
                  label={tTabs("admin")}
                  icon={NAVBAR_ICONS.admin}
                  isActive={currentPath === "/admin"}
                  onClick={handleNavClick}
                  activeColor={activeColor}
                />
              </>
            )}
          </List>
          <UserMenu label={userDisplayLabel} />
        </Box>
      </Drawer>
    </Box>
  );
}
